import {
    BRAND,
    CONTEXT,
    FINANCE_WIDGET_CONTEXT_COLOUR,
    IS_B2B,
    isBrandAP,
    isBrandOV,
    isMarketFR,
    isMarketGB,
    isMarketIT,
} from '../constants/main';
import {
    CUSTOMER_TYPE_MAPPING,
    JOURNEY_TYPE,
    LOADING_WEIGHT,
    PROMO_CODE_CUSTOMER_TYPE_MAPPING,
    SITE_CODE,
} from '@components/FinanceWidget/shared';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { IConfigurableCar, IConfigurableCarExtended, IDeal } from '../interfaces/Car';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../context/featureSwitchApp';
import {
    IFinanceWidgetContext,
    IFinanceWidgetOutput,
    IFinanceWidgetParameters,
    IFinanceWidgetParametersParsed,
} from '../redux/financeWidget/financeWidget.duck.interface';
import UIDuck from '../redux/commonDucks/ui.duck';
import { captureException } from '@utils/sentry.utils';
import {
    buildOtrCostsIncluded,
    formatColorToAccessory,
    formatFinanceQuotePostRequestData,
    getOptionData,
    getStockOptionsData,
    parseFinanceWidgetOutput,
} from '@utils/FinanceWidget.utils';
import FinanceWidgetDuck from '../redux/financeWidget/financeWidget.duck';
import routes from '../constants/routes';
import { useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useRouter } from 'next/router';
import { ICarDetail } from '../redux/carDetails/carDetails.duck.interface';
import { IFinanceQuote } from '../services/calculateSummary/calculateSummary.types';
import { FINANCE_JOURNEY } from '../types/journey';
import FinanceCalculatorService from '../services/financeQuote/financePrices.service';
import { CarJourneyType, DealService } from '../services';
import { FilterDuck } from 'src/redux/filters/filter.duck';
import { getSessionIdCookie } from '@utils/Session.utils';
import { getGeolocationExtraFileds } from '@utils/Deal.utils';
import carDetailsDuck from '../redux/carDetails/carDetails.duck';
import { PaymentJourneyTypes } from '../partExchange/interfaces/Default';
import { useExternalCheckout } from '@hooks/useExternalCheckout';
import { Redux } from '../redux/redux.interface';
import {
    CONSUMPTION_CO2_KEY,
    CONSUMPTION_KEY_WLTP,
    FISCAL_HORSE_POWER,
    MAXIMUM_POWER_KW,
} from '../constants/characteristics';
import { IConfiguratorState } from '../redux/configurator/configurator.duck.interface';
import { IOption } from '../services/stock/models/stockItems.service.model';
import ConfiguratorDuck from '../redux/configurator/configurator.duck';
import Routes from '../constants/routes';

const POWER_CATEGORY_KEY = 'power';

export const useFinanceWidgetLogic = (generatedIndex: number) => {
    const dispatch = useDispatch();

    const router = useRouter();

    const { carDetails, currentDeal } = useCarDetailsDuck();
    const { isStockJourney } = useCarStockJourneyCheck();
    const { externalCheckoutEnabled } = useExternalCheckout();

    const financeQuote = useSelector((state: Redux) => FinanceWidgetDuck.getFinanceQuote(state));
    const configurator = useSelector((state: Redux) => ConfiguratorDuck.getOwnState(state));

    const [isLoading, setIsLoading] = useState(false);

    const isApplyMaximumPowerEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_APPLY_MAXIMUM_POWER);

    const isApplyAdministrativePowerEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_APPLY_ADMINISTRATIVE_POWER
    );

    const isPromoCodeCustomerTypeEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_PROPAGATE_PROMO_CODE_CUSTOMER_TYPE
    );

    const isFinanceWidgetOmegaEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_FINANCE_WIDGET_OMEGA_ENABLED
    );

    const {
        lcdv16,
        language,
        title,
        market,
        model,
        engine,
        specPack,
        gearbox,
        images,
        vehicleUseId,
        extraFields,
        technicalSpecifications,
        options: carOptions,
    } = carDetails;

    const updateDeal = async (carDetails: ICarDetail, parameters: IFinanceWidgetParametersParsed, currency: string) => {
        let newFinanceQuote: IFinanceQuote;
        let newDeal: IDeal<IConfigurableCarExtended>;
        // check if finance deal, because default can be cash, but car with finance is available
        if (currentDeal.journeyType === FINANCE_JOURNEY) {
            const { data } = await FinanceCalculatorService.createFinanceQuote(
                formatFinanceQuotePostRequestData(carDetails, parameters, currency, currentDeal)
            );

            newFinanceQuote = data;
            newDeal = {
                ...(currentDeal as unknown as IDeal<IConfigurableCarExtended>),
                financeQuoteId: newFinanceQuote.token,
                extraFields: {
                    ...currentDeal?.extraFields,
                    ...(router.pathname.includes(Routes.BASKET) && { hasSelectedPaymentJourney: true }),
                },
            };
        } else {
            const configId = carDetails.stock ? carDetails.orders[0].uuid : carDetails.externalId ?? carDetails._id;
            // switch to finance deal with custom params and fetch FQ
            const { data: dealResponse } = await DealService.setConfiguration(
                getSessionIdCookie(),
                configId,
                'finance',
                carDetails.stock ? CarJourneyType.STOCK : CarJourneyType.CONFIGURABLE,
                {
                    financeGatewayParameters: parameters,
                    useExternalCheckout: externalCheckoutEnabled,
                    ...(router.pathname.includes(Routes.BASKET) && { hasSelectedPaymentJourney: true }),
                    ...getGeolocationExtraFileds(currentDeal),
                }
            );
            if (dealResponse?.success) {
                newDeal = dealResponse.data.deal;
                newFinanceQuote = (await FinanceCalculatorService.getFinanceQuote(newDeal.financeQuoteId)).data;
            }
        }

        // We should remove parameters if the finance quote personalization fails
        const newFinanceWidgetOutput: IFinanceWidgetOutput | null = newFinanceQuote.financeGatewayParameters
            ? {
                  parameters: newFinanceQuote.financeGatewayParameters,
                  widgetParameters: {},
              }
            : null;
        batch(() => {
            dispatch(FinanceWidgetDuck.setFinanceQuote(newFinanceQuote ?? financeQuote));
            dispatch(FinanceWidgetDuck.setFinanceWidgetOutput(newFinanceWidgetOutput));
            dispatch(carDetailsDuck.setCurrentDeal((newDeal ?? currentDeal) as IDeal<IConfigurableCarExtended>));
            dispatch(FilterDuck.changeBudgetType(PaymentJourneyTypes.FINANCE));
            dispatch(FilterDuck.wasSetBudgetType(true));
        });

        return;
    };

    const handlePrice = (parameters: IFinanceWidgetParametersParsed, currency: string) => {
        setIsLoading(true);
        dispatch(UIDuck.setLoading(LOADING_WEIGHT));

        updateDeal(carDetails, parameters, currency)
            .then(() => {
                setIsLoading(false);
            })
            .catch((e) => {
                captureException(e);
                console.error(e);
                setIsLoading(false);
            })
            .finally(() => {
                dispatch(UIDuck.setLoading(-LOADING_WEIGHT));
            });
    };

    const handleWidgetOutput = (output: IFinanceWidgetOutput, currency: string) => {
        const outputParsed = parseFinanceWidgetOutput(output);

        dispatch(FinanceWidgetDuck.setFinanceWidgetOutput(outputParsed));
        if (router.pathname.includes(routes.BASKET) || router.pathname.includes(routes.SUMMARY)) {
            handlePrice(outputParsed.parameters, currency);
        }
    };

    const getJourneyTypeContext = () => {
        if (IS_B2B) {
            if (isMarketFR && isBrandAP) {
                //APP-17013
                return JOURNEY_TYPE.B2B_FR_AP;
            }
            return JOURNEY_TYPE.B2B;
        }
        return isStockJourney ? JOURNEY_TYPE.B2C_STOCK : JOURNEY_TYPE.B2C;
    };

    const getClientType = (currentDeal: IDeal): string => {
        if (isPromoCodeCustomerTypeEnabled) {
            let promoCode = null;
            try {
                promoCode = JSON.parse(currentDeal?.extraFields?.promoCode);
            } catch (e: any) {
                console.error('Unable to parse promoCode from deal:', currentDeal);
                return CUSTOMER_TYPE_MAPPING[CONTEXT];
            }

            if (promoCode && promoCode.customerType) {
                return PROMO_CODE_CUSTOMER_TYPE_MAPPING[promoCode.customerType];
            }
        }
        return CUSTOMER_TYPE_MAPPING[CONTEXT];
    };

    const getOptions = (configurator: IConfiguratorState, carOptions: IOption[], isStockJourney: boolean) => {
        if (isStockJourney) {
            return getStockOptionsData(carOptions);
        }

        return configurator.selectedOptions.map((option) => getOptionData(carOptions, option)).filter(Boolean);
    };

    const getMaximumPowers = () => {
        const IceKey = 'icemax';
        const ElectricKey = 'electricmax';
        const CombinedKey = 'combinedmax';

        return carDetails?.trimAttributes
            ?.filter((attr: { category: string; attributeCode: string }) => {
                const parsedAttributeCode = attr?.attributeCode?.split('-');

                return (
                    attr?.category === POWER_CATEGORY_KEY &&
                    (parsedAttributeCode?.[0] === IceKey ||
                        parsedAttributeCode?.[0] === ElectricKey ||
                        parsedAttributeCode?.[0] === CombinedKey)
                );
            })
            ?.map((attr: { attributeCode: string; title: string }) => {
                const parsedAttributeCode = attr.attributeCode?.split('-');

                const response = {
                    unit: parsedAttributeCode[1].toUpperCase(),
                    value: Number(attr.title),
                };

                if (parsedAttributeCode[0] === IceKey) {
                    return { ...response, key: 'ICE' };
                }

                if (parsedAttributeCode[0] === ElectricKey) {
                    return { ...response, key: 'electric' };
                }

                if (parsedAttributeCode[0] === CombinedKey) {
                    return { ...response, key: 'combined' };
                }
            });
    };

    const fullProductConfiguration = currentDeal?.fullProductConfiguration || carDetails;
    const co2Combined = technicalSpecifications
        ?.find((spec: any) => spec.key === CONSUMPTION_KEY_WLTP)
        ?.data?.find((technicalProperty: any) => technicalProperty.key === CONSUMPTION_CO2_KEY);
    const characteristics = fullProductConfiguration?.characteristics ?? [];
    const maximumPowerKW = characteristics.find((ch: any) => ch.key === MAXIMUM_POWER_KW);
    const fiscalHP = characteristics.find((ch: any) => ch.key === FISCAL_HORSE_POWER);
    const co2Emissions = co2Combined?.value ? parseInt(co2Combined.value) : 0;
    const transportationCostInclTax = fullProductConfiguration?.importInputs?.transportationCostInclTax;
    const registrationFeePercentage = fullProductConfiguration?.importInputs?.registrationFeePercentage;
    const tfm = fullProductConfiguration?.importInputs?.tfm;
    const ecoBonus = fullProductConfiguration?.importInputs?.ecologicalBonus ?? 0;
    const kind = vehicleUseId ?? extraFields?.vehicleUseId ?? 'VP';
    const energy = carDetails?.fuelCode;
    const maximumPowerHP = isApplyMaximumPowerEnabled && maximumPowerKW ? maximumPowerKW.value : undefined;
    const fiscalHorsePower = isApplyAdministrativePowerEnabled && fiscalHP ? parseFloat(fiscalHP.value) : undefined;
    const otrCostsIncluded = buildOtrCostsIncluded(tfm, transportationCostInclTax);
    const isMarketGBAndBrandOV = isMarketGB && isBrandOV;
    const shouldSendKindEnergyVX = isMarketGBAndBrandOV && isFinanceWidgetOmegaEnabled;

    let colour: string;
    if (isBrandOV && isFinanceWidgetOmegaEnabled) {
        colour = FINANCE_WIDGET_CONTEXT_COLOUR;
    } else if (!isBrandOV) {
        colour = FINANCE_WIDGET_CONTEXT_COLOUR;
    }

    const getContext = (
        basePrice: number,
        netPrice: number,
        registrationFee: number,
        vehicleExciseDuty: number,
        deliveryInclTax: number,
        financeWidgetParameters: IFinanceWidgetParameters
    ): IFinanceWidgetContext => {
        const parsedMaximumPowers = getMaximumPowers();

        return {
            generatedIndex,
            context: {
                siteCode: SITE_CODE,
                journeyType: getJourneyTypeContext(),
                distributionBrand: BRAND,
                ...(market && { countryCode: market.toUpperCase() }),
                languageCode: language,
                omega: Boolean(isFinanceWidgetOmegaEnabled),
                customer: {
                    clientType: getClientType(currentDeal),
                },
                ...(colour && { colour }),
            },
            vehicle: {
                ...(lcdv16 && { lcdv16Code: lcdv16.replace(/\s/g, '') }),
                ...((basePrice || netPrice) && {
                    pricing: {
                        ...(basePrice && { basicPriceInclTax: basePrice }),
                        ...(netPrice && { netPriceInclTax: netPrice }),
                    },
                }),
                fiscalProperties: {
                    co2Emissions,
                    wltpCo2Emissions: co2Emissions,
                    ...(!isMarketIT && registrationFeePercentage && { immRate: registrationFeePercentage }),
                    ...(registrationFee && { registrationFees: registrationFee }),
                    ...(vehicleExciseDuty && { roadFundLicence: vehicleExciseDuty }),
                    ...(isMarketIT && ecoBonus > 0 && { ecoBonus }),
                    ...(fiscalHorsePower && { fiscalHorsePower }),
                },
                ...(deliveryInclTax && {
                    otrCosts: {
                        delivery: {
                            amountInclTax: deliveryInclTax,
                        },
                    },
                }),
                otrCostsIncluded,
                features: {
                    ...(parsedMaximumPowers?.length > 0 && { maximumPowers: getMaximumPowers() }),
                    ...((!isMarketGBAndBrandOV || shouldSendKindEnergyVX) &&
                        kind &&
                        energy && {
                            kind,
                            energy,
                            ...(maximumPowerHP && { maximumPowerHP }),
                        }),
                },
                options: [
                    ...getOptions(configurator, carOptions, isStockJourney),
                    ...[
                        formatColorToAccessory(carDetails.interiorColour),
                        formatColorToAccessory(carDetails.exteriorColour),
                    ],
                ],
                presentation: {
                    ...(title && { longLabel: title }),
                    ...(model?.title && { famillyLabel: model.title }),
                    ...(specPack?.title && { gradeLabel: specPack.title }),
                    ...(engine?.title && { engineLabel: engine.title }),
                    ...(gearbox?.title && { transmissionLabel: gearbox.title }),
                    logo: images ? images[0]?.url : '',
                },
            },
            ...(Object.keys(financeWidgetParameters ?? {}).length !== 0 && {
                parameters: financeWidgetParameters,
            }),
        };
    };

    return {
        handleWidgetOutput,
        getContext,
        isLoading,
    };
};
